<script>
import axios from '@/utils/axios';
import { allUsers } from '@/utils/user';


export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      id: null,
      users: [],
      user_id: '',
      states: [],
      state: '',
      cities: [],
      selected_cities: [],
      // For edit mode (single city)
      edit_city: '',
      isEdit: false,
    };
  },

  methods: {
 
    async getUsers() {
      try {
        const response = await allUsers();
        this.users = response.data.data;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      }
    },

    async getStates() {
      try {
        const response = await axios.get('/states/101');
        this.states = response.data.data;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      }
    },

    async getCities() {
      if (!this.state) {
        this.cities = [];
        this.selected_cities = [];
        return;
      }
      try {
        const response = await axios.get(`/cities/${this.state}`);
        this.cities = response.data.data;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      }
    },

    onStateChange() {
      this.selected_cities = [];
      this.getCities();
    },

    openModal() {
      this.resetForm();
      this.isEdit = false;
      this.getUsers();
      this.getStates();
    },

    editRule(row) {
      this.resetForm();
      this.isEdit = true;
      this.getUsers();
      this.getStates();
      this.id = row.id;
      this.user_id = row.user_id;
      this.edit_city = row.city;
    },

    confirmDelete(row) {
      this.id = row.id;
    },

    async saveRule() {
      this.errors = {};

      if (!this.user_id) {
        this.errors.user_id = ['Please select a user.'];
        return;
      }

      if (this.isEdit) {
        // Edit mode — update single rule
        if (!this.edit_city) {
          this.errors.city = ['Please enter a city.'];
          return;
        }
        this.setLoading(true);
        try {
          const response = await axios.put(`/lead-assignment-location-rules/${this.id}`, {
            user_id: this.user_id,
            city: this.edit_city,
          });
          this.$swalToast(response.data.message);
          this.$emit('updated');
          this.$closeModal('location_rule_modal');
          this.resetForm();
        } catch (error) {
          if (error.type == 'validation') {
            this.errors = error.errors;
          } else {
            this.$swalToast(error.message, 'error');
          }
        } finally {
          this.setLoading(false);
        }
      } else {
        // Create mode — one rule per selected city
        if (!this.selected_cities.length) {
          this.errors.cities = ['Please select at least one city.'];
          return;
        }
        this.setLoading(true);
        let successCount = 0;
        let lastError = null;
        try {
          for (const city of this.selected_cities) {
            try {
              await axios.post('/lead-assignment-location-rules', {
                user_id: this.user_id,
                city: city,
              });
              successCount++;
            } catch (error) {
              if (error.type == 'validation') {
                lastError = error;
              } else {
                lastError = error;
              }
            }
          }
          if (successCount > 0) {
            this.$swalToast(`${successCount} location rule(s) created successfully.`);
            this.$emit('updated');
            this.$closeModal('location_rule_modal');
            this.resetForm();
          }
          if (lastError) {
            if (lastError.type == 'validation') {
              this.errors = lastError.errors;
            } else {
              this.$swalToast(lastError.message, 'error');
            }
          }
        } finally {
          this.setLoading(false);
        }
      }
    },

    async deleteRule() {
      this.setLoading(true);
      try {
        const response = await axios.delete(`/lead-assignment-location-rules/${this.id}`);
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('location_delete_modal');
      } catch (error) {
        this.$swalToast(error.message, 'error');
        this.$closeModal('location_delete_modal');
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      this.id = null;
      this.errors = {};
      this.user_id = '';
      this.state = '';
      this.cities = [];
      this.selected_cities = [];
      this.edit_city = '';
      this.isEdit = false;
    },
  },
};
</script>


<template>
  <!-- Add/Edit Modal -->
  <div class="modal fade" id="location_rule_modal">
    <div class="modal-dialog modal-dialog-centered modal-md w-100">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ isEdit ? 'Edit' : 'Add' }} Location Rule</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="saveRule">
          <div class="modal-body pb-0">
            <div class="row">

              <!-- User -->
              <div class="col-12 mb-3">
                <label class="form-label fw-semibold">Users <span class="text-danger">*</span></label>
                <vue-select :options="users" label="name" placeholder="Select User" :reduce="u => u.id" v-model="user_id" />
                <div v-if="errors.user_id" class="text-danger small mt-1">{{ errors.user_id[0] }}</div>
              </div>

              <!-- Create Mode: State + Cities multi-select -->
              <template v-if="!isEdit">
                <div class="col-12 mb-3">
                  <label class="form-label fw-semibold">State <span class="text-danger">*</span></label>
                  <vue-select :options="states" label="name" placeholder="Select State" :reduce="s => s.name" v-model="state" @update:modelValue="onStateChange" />
                  <div v-if="errors.state" class="text-danger small mt-1">{{ errors.state[0] }}</div>
                </div>

                <div class="col-12 mb-3" v-if="cities.length">
                  <label class="form-label fw-semibold">Cities <span class="text-danger">*</span> 
                    <span class="text-muted small fw-normal ms-1">({{ selected_cities.length }} selected)</span>
                  </label>
                  <vue-select :options="cities" v-model="selected_cities" multiple placeholder="Select Cities" :close-on-select="false"/>
                  <div v-if="errors.cities" class="text-danger small mt-1">{{ errors.cities[0] }}</div>
                  <div v-if="errors.city" class="text-danger small mt-1">{{ errors.city[0] }}</div>
                </div>

                <div class="col-12 mb-3" v-if="state && !cities.length">
                  <div class="text-muted small">No cities found for this state.</div>
                </div>
              </template>

              <!-- Edit Mode: Single city input -->
              <template v-if="isEdit">
                <div class="col-12 mb-3">
                  <label class="form-label fw-semibold">City <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="edit_city" placeholder="City name" />
                  <div v-if="errors.city" class="text-danger small mt-1">{{ errors.city[0] }}</div>
                </div>
              </template>

            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">
              {{ isEdit ? 'Update' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add/Edit Modal -->

  <!-- Delete Modal -->
  <div class="modal fade" id="location_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">You want to delete this location rule, this can't be undone once you delete.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteRule">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
</template>